import { useEffect, useState } from "react";
import { API_BASE } from "../api/config";
import Layout from "../components/Layout";
import { NavLink } from "react-router-dom";
import "./DeliveryFreeRulesPage.css";

const SHOP_GROUPS = {
  PRETTYFIT: ["OSPF", "MDYPF", "JCPF"],
  HANGTEN: ["OSHT", "MDYHT", "JCHT", "GMP", "JS", "MT"],
};

const ALL_SHOPS = [
  "OSPF",
  "MDYPF",
  "JCPF",
  "OSHT",
  "MDYHT",
  "JCHT",
  "GMP",
  "JS",
  "MT",
  "STPF",
  "STHT",
];

const emptyForm = {
  name: "",
  shopGroup: "PRETTYFIT",
  ruleType: "ITEM_QTY",
  applyShops: ["OSPF", "MDYPF", "JCPF"],
  minQty: 2,
  minAmount: 0,
  startDate: "",
  endDate: "",
  active: true,
};

export default function DeliveryFreeRulesPage() {
  const [rules, setRules] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadRules = async () => {
    try {
      setLoading(true);

      const res = await fetch(`${API_BASE}/api/delivery-free-rules`);
      const data = await res.json();

      if (!res.ok || data.success === false) {
        alert(data.message || "Load free shipping rules failed");
        return;
      }

      setRules(data.rules || []);
    } catch (err) {
      console.error("Load free shipping rules error:", err);
      alert("Load free shipping rules failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRules();
  }, []);

  const handleShopGroupChange = (group) => {
    const shops = SHOP_GROUPS[group] || [];

    setForm((prev) => ({
      ...prev,
      shopGroup: group,
      applyShops: shops,
      name:
        group === "PRETTYFIT"
          ? "PrettyFit 2 pcs Free Delivery"
          : "Hangten 300000 Ks Free Delivery",
      ruleType: group === "PRETTYFIT" ? "ITEM_QTY" : "SALE_AMOUNT",
      minQty: group === "PRETTYFIT" ? 2 : 0,
      minAmount: group === "HANGTEN" ? 300000 : 0,
    }));
  };

  const toggleShop = (shop) => {
    setForm((prev) => {
      const exists = prev.applyShops.includes(shop);

      return {
        ...prev,
        applyShops: exists
          ? prev.applyShops.filter((s) => s !== shop)
          : [...prev.applyShops, shop],
      };
    });
  };

  const resetForm = () => {
    setForm(emptyForm);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.name.trim()) {
      alert("Rule name လိုပါတယ်");
      return;
    }

    if (form.applyShops.length === 0) {
      alert("Apply shop အနည်းဆုံး ၁ ခုရွေးပါ");
      return;
    }

    if (form.ruleType === "ITEM_QTY" && Number(form.minQty || 0) <= 0) {
      alert("Minimum Qty လိုပါတယ်");
      return;
    }

    if (form.ruleType === "SALE_AMOUNT" && Number(form.minAmount || 0) <= 0) {
      alert("Minimum Amount လိုပါတယ်");
      return;
    }

    try {
      setSaving(true);

      const res = await fetch(`${API_BASE}/api/delivery-free-rules`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...form,
          minQty: Number(form.minQty || 0),
          minAmount: Number(form.minAmount || 0),
        }),
      });

      const data = await res.json();

      if (!res.ok || data.success === false) {
        alert(data.message || "Save rule failed");
        return;
      }

      alert("Free shipping rule saved ✅");
      resetForm();
      await loadRules();
    } catch (err) {
      console.error("Save free shipping rule error:", err);
      alert("Save rule failed");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (rule) => {
    try {
      const res = await fetch(
        `${API_BASE}/api/delivery-free-rules/${encodeURIComponent(rule.id)}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            active: !rule.active,
          }),
        },
      );

      const data = await res.json();

      if (!res.ok || data.success === false) {
        alert(data.message || "Update failed");
        return;
      }

      await loadRules();
    } catch (err) {
      console.error("Toggle rule error:", err);
      alert("Update failed");
    }
  };

  const deleteRule = async (rule) => {
    const ok = window.confirm(`Delete rule "${rule.name}"?`);
    if (!ok) return;

    try {
      const res = await fetch(
        `${API_BASE}/api/delivery-free-rules/${encodeURIComponent(rule.id)}`,
        {
          method: "DELETE",
        },
      );

      const data = await res.json();

      if (!res.ok || data.success === false) {
        alert(data.message || "Delete failed");
        return;
      }

      await loadRules();
    } catch (err) {
      console.error("Delete rule error:", err);
      alert("Delete failed");
    }
  };

  return (
    <Layout title="Delivery System">
      <div className="delivery-free-page">
        <div className="delivery-tabs">
          <NavLink to="/erp/delivery-rates">Delivery Rates</NavLink>
          <NavLink to="/erp/delivery-free-rules">Free Shipping Rules</NavLink>
        </div>

        <div className="delivery-free-header">
          <div>
            <h2>Free Shipping Rules</h2>
            <p>
              Shop filter, item qty, sale amount အလိုက် delivery free rule
              ပြင်နိုင်ပါတယ်။
            </p>
          </div>

          <button type="button" onClick={loadRules}>
            Refresh
          </button>
        </div>

        <form className="delivery-free-card" onSubmit={handleSubmit}>
          <h3>Create / Update Rule</h3>

          <div className="delivery-free-grid">
            <label>
              Rule Name
              <input
                value={form.name}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, name: e.target.value }))
                }
                placeholder="PrettyFit 2 pcs Free Delivery"
              />
            </label>

            <label>
              Shop Group
              <select
                value={form.shopGroup}
                onChange={(e) => handleShopGroupChange(e.target.value)}
              >
                <option value="PRETTYFIT">PRETTYFIT</option>
                <option value="HANGTEN">HANGTEN</option>
              </select>
            </label>

            <label>
              Rule Type
              <select
                value={form.ruleType}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    ruleType: e.target.value,
                  }))
                }
              >
                <option value="ITEM_QTY">Item Qty</option>
                <option value="SALE_AMOUNT">Sale Amount</option>
              </select>
            </label>

            <label>
              Minimum Qty
              <input
                type="number"
                value={form.minQty}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, minQty: e.target.value }))
                }
              />
            </label>

            <label>
              Minimum Amount
              <input
                type="number"
                value={form.minAmount}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, minAmount: e.target.value }))
                }
              />
            </label>

            <label>
              Start Date
              <input
                type="date"
                value={form.startDate}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, startDate: e.target.value }))
                }
              />
            </label>

            <label>
              End Date
              <input
                type="date"
                value={form.endDate}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, endDate: e.target.value }))
                }
              />
            </label>

            <label className="delivery-free-check">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, active: e.target.checked }))
                }
              />
              Active
            </label>
          </div>

          <div className="delivery-shop-box">
            <strong>Apply Shops</strong>

            <div className="delivery-shop-list">
              {ALL_SHOPS.map((shop) => (
                <label
                  key={shop}
                  className={
                    form.applyShops.includes(shop)
                      ? "delivery-shop active"
                      : "delivery-shop"
                  }
                >
                  <input
                    type="checkbox"
                    checked={form.applyShops.includes(shop)}
                    onChange={() => toggleShop(shop)}
                  />
                  {shop}
                </label>
              ))}
            </div>
          </div>

          <div className="delivery-free-actions">
            <button type="button" onClick={resetForm}>
              Reset
            </button>

            <button type="submit" disabled={saving}>
              {saving ? "Saving..." : "Save Rule"}
            </button>
          </div>
        </form>

        <div className="delivery-free-card">
          <h3>Rule List</h3>

          {loading ? (
            <div className="delivery-empty">Loading...</div>
          ) : rules.length === 0 ? (
            <div className="delivery-empty">No rules</div>
          ) : (
            <div className="delivery-table-wrap">
              <table className="delivery-free-table">
                <thead>
                  <tr>
                    <th>Rule Name</th>
                    <th>Group</th>
                    <th>Shops</th>
                    <th>Type</th>
                    <th>Min Qty</th>
                    <th>Min Amount</th>
                    <th>Date</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {rules.map((rule) => (
                    <tr key={rule.id}>
                      <td>{rule.name}</td>
                      <td>{rule.shopGroup}</td>
                      <td>{(rule.applyShops || []).join(", ")}</td>
                      <td>{rule.ruleType}</td>
                      <td>{Number(rule.minQty || 0)}</td>
                      <td>{Number(rule.minAmount || 0).toLocaleString()} Ks</td>
                      <td>
                        {rule.startDate || "-"} → {rule.endDate || "-"}
                      </td>
                      <td>
                        <span
                          className={
                            rule.active
                              ? "delivery-status active"
                              : "delivery-status inactive"
                          }
                        >
                          {rule.active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td>
                        <div className="delivery-row-actions">
                          <button type="button" onClick={() => toggleActive(rule)}>
                            {rule.active ? "Disable" : "Enable"}
                          </button>

                          <button
                            type="button"
                            className="danger"
                            onClick={() => deleteRule(rule)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
